import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config';

export interface FastingSession {
  id: string;
  userId: string;
  startTime: Date;
  endTime?: Date;
  breakTime?: Date; // When the user broke their fast
  targetHours: number; // e.g., 16 for 16:8 fasting
  status: 'active' | 'completed' | 'stopped';
  createdAt: Date;
  updatedAt: Date;
}

export interface FastingSettings {
  userId: string;
  defaultPlan: string; // '16:8', '18:6', '20:4', 'custom'
  customHours?: number;
  notifications?: boolean;
  updatedAt: Date;
}

/**
 * Get the active fasting session for a user
 */
export async function getActiveFastingSession(userId: string): Promise<FastingSession | null> {
  try {
    const q = query(
      collection(db, 'fastingSessions'),
      where('userId', '==', userId),
      where('status', '==', 'active'),
      orderBy('startTime', 'desc'),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    return {
      id: doc.id,
      userId: data.userId,
      startTime: data.startTime.toDate(),
      endTime: data.endTime?.toDate(),
      breakTime: data.breakTime?.toDate(),
      targetHours: data.targetHours,
      status: data.status,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  } catch (error) {
    console.error('Error fetching active fasting session:', error);
    throw error;
  }
}

/**
 * Start a new fasting session
 */
export async function startFastingSession(
  userId: string, 
  targetHours: number = 16
): Promise<FastingSession> {
  try {
    // Check if there's an active session
    const activeSession = await getActiveFastingSession(userId);
    if (activeSession) {
      throw new Error('There is already an active fasting session');
    }

    const sessionRef = doc(collection(db, 'fastingSessions'));
    const now = new Date();
    const sessionData = {
      userId,
      startTime: Timestamp.fromDate(now),
      targetHours,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(sessionRef, sessionData);

    return {
      id: sessionRef.id,
      userId,
      startTime: now,
      targetHours,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error('Error starting fasting session:', error);
    throw error;
  }
}

/**
 * Stop the current fasting session
 */
export async function stopFastingSession(userId: string): Promise<void> {
  try {
    const activeSession = await getActiveFastingSession(userId);
    if (!activeSession) {
      throw new Error('No active fasting session found');
    }

    const sessionRef = doc(db, 'fastingSessions', activeSession.id);
    const now = new Date();

    await updateDoc(sessionRef, {
      status: 'stopped',
      endTime: Timestamp.fromDate(now),
      breakTime: Timestamp.fromDate(now), // Record when they broke the fast
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error stopping fasting session:', error);
    throw error;
  }
}

/**
 * Complete a fasting session (when target is reached)
 */
export async function completeFastingSession(userId: string): Promise<void> {
  try {
    const activeSession = await getActiveFastingSession(userId);
    if (!activeSession) {
      throw new Error('No active fasting session found');
    }

    const sessionRef = doc(db, 'fastingSessions', activeSession.id);
    const now = new Date();

    await updateDoc(sessionRef, {
      status: 'completed',
      endTime: Timestamp.fromDate(now),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error completing fasting session:', error);
    throw error;
  }
}

/**
 * Get fasting settings for a user
 */
export async function getFastingSettings(userId: string): Promise<FastingSettings | null> {
  try {
    const settingsRef = doc(db, 'fastingSettings', userId);
    const settingsDoc = await getDoc(settingsRef);

    if (!settingsDoc.exists()) {
      // Return default settings
      return {
        userId,
        defaultPlan: '16:8',
        notifications: false,
        updatedAt: new Date(),
      };
    }

    const data = settingsDoc.data();
    return {
      userId,
      defaultPlan: data.defaultPlan || '16:8',
      customHours: data.customHours,
      notifications: data.notifications ?? false,
      updatedAt: data.updatedAt.toDate(),
    };
  } catch (error) {
    console.error('Error fetching fasting settings:', error);
    throw error;
  }
}

/**
 * Update fasting settings for a user
 */
export async function updateFastingSettings(
  userId: string,
  settings: Partial<Omit<FastingSettings, 'userId' | 'updatedAt'>>
): Promise<void> {
  try {
    const settingsRef = doc(db, 'fastingSettings', userId);
    await setDoc(
      settingsRef,
      {
        ...settings,
        userId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error updating fasting settings:', error);
    throw error;
  }
}

/**
 * Get fasting history for a user
 */
export async function getFastingHistory(
  userId: string,
  limitCount: number = 10
): Promise<FastingSession[]> {
  try {
    const q = query(
      collection(db, 'fastingSessions'),
      where('userId', '==', userId),
      orderBy('startTime', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        startTime: data.startTime.toDate(),
        endTime: data.endTime?.toDate(),
        breakTime: data.breakTime?.toDate(),
        targetHours: data.targetHours,
        status: data.status,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    });
  } catch (error) {
    console.error('Error fetching fasting history:', error);
    throw error;
  }
}

